/* eslint-disable jsx-a11y/label-has-for */
/* eslint-disable jsx-a11y/label-has-associated-control */
import PropTypes from 'prop-types';
import React, { useMemo } from 'react';
import { useDarkMode, withStyles } from '@glu/theming';
import { withInputAPI } from '@glu/validation-react';
import Error from '../Error/Error';
import LengthIndicator from '../LengthIndicator/LengthIndicator';
import LabelText from '../LabelText/LabelText';
import OptionalInlineText from '../OptionalInlineText/OptionalInlineText';
import InputComponent from './InputComponent';
import styles from './Input.styles';
import HelpText from '../HelpText/HelpText';

let uniqueId = 0;

const propTypes = {

  /** Boolean to indicate whether the input should receive focus */
  autoFocus: PropTypes.bool,

  /** Custom classes applied to label wrapping component */
  className: PropTypes.string,

  /** Classes provided by the theme */
  classes: PropTypes.objectOf(PropTypes.string).isRequired,

  /** dark mode boolean */
  dark: PropTypes.bool,

  /** The error to display */
  error: PropTypes.string,

  /** Full errors onject. */
  errors: PropTypes.arrayOf(PropTypes.string),

  /** Help text to display with input */
  helpText: PropTypes.string,

  /** Id for the input if a randomly generated id isn't desired */
  htmlId: PropTypes.string,

  /** Boolean to indicate whether or not you want the standard inline layout */
  inline: PropTypes.bool,

  /** Custom classes applied to input element */
  inputClass: PropTypes.string,

  /** Custom classes applied to label span element */
  labelSpanClass: PropTypes.string,

  /** Text for the span inside the label */
  labelText: PropTypes.oneOfType([PropTypes.string, PropTypes.node]).isRequired,

  /** maxLengthIndicatorLimit is used to display value length counter
   *  shows maximal value length limit */
  maxLengthIndicatorLimit: PropTypes.number,

  /** Name attribute */
  name: PropTypes.string.isRequired,

  /** Shows optional text on label */
  optional: PropTypes.bool,

  /** Boolean controlling whether the labelText should only be shown to screen readers */
  screenReaderLabel: PropTypes.bool,

  /** Boolean to indicate whether to render a text area instead */
  textarea: PropTypes.bool,

  /** Theme for styling */
  theme: PropTypes.objectOf(PropTypes.any),

  /** The html value attribute for the input containing the user entered data */
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),

  /** Use warning styles if true */
  warning: PropTypes.bool
};

const defaultProps = {
  autoFocus: false,
  className: '',
  dark: false,
  error: '',
  errors: undefined,
  helpText: undefined,
  htmlId: null,
  inline: false,
  inputClass: '',
  labelSpanClass: '',
  maxLengthIndicatorLimit: undefined,
  optional: false,
  screenReaderLabel: false,
  textarea: false,
  theme: undefined,
  value: '',
  warning: false
};

const Input = ({
  className,
  classes,
  dark: propDark,
  error,
  errors,
  helpText,
  htmlId,
  inline,
  inputClass,
  labelSpanClass,
  labelText,
  maxLengthIndicatorLimit,
  name,
  optional,
  screenReaderLabel,
  textarea,
  theme: _theme,
  /* These shouldn't be passed in as a prop to the input. underscore indicates not used */
  warning,
  ...props
}) => {
  // eslint-disable-next-line no-plusplus
  const id = useMemo(() => htmlId || `input-${uniqueId++}`, [htmlId]);
  const errorMessage = error || errors?.[0];
  const darkMode = useDarkMode();
  const dark = propDark || darkMode;
  return (
    <label htmlFor={id} className={`${className} ${classes.root}`}>
      <LabelText
        className={labelSpanClass}
        screenReaderLabel={screenReaderLabel}
        label={labelText}
        hasError={!!errorMessage}
        optional={optional}
        name={name}
        inline={inline}
        textarea={textarea}
        warning={warning}
      />
      <div className={classes.wrap}>
        <InputComponent
          id={id}
          {...props}
          inputClass={`${inputClass}`}
          textarea={textarea}
          error={errorMessage}
          warning={warning}
          dark={dark}
          name={name}
        />
        <div className={classes.helpWrapper}>
          <Error name={name} error={errorMessage} warning={warning} dark={dark} />
          <LengthIndicator
            value={props.value}
            hasError={!!errorMessage}
            dark={dark}
            maxLengthIndicatorLimit={maxLengthIndicatorLimit}
          />
          {helpText && <HelpText>{helpText}</HelpText>}
        </div>
      </div>
      {optional && inline
        ? <OptionalInlineText hasError={!!errorMessage} warning={warning} withTextarea={textarea} />
        : null}
    </label>
  );
};

Input.propTypes = propTypes;
Input.defaultProps = defaultProps;

const StyledInput = withStyles(styles)(Input);
const InputWithInputAPI = withInputAPI(StyledInput);
InputWithInputAPI.displayName = 'Input';

export default InputWithInputAPI;
export const ValidatedInput = InputWithInputAPI;
export const BaseInput = Input;
